import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Order } from '../types';

interface LabTrendChartProps {
  orders: Order[];
  metricName: string; // e.g., 'HbA1c'
  regex: RegExp;
  color?: string;
}

export const LabTrendChart: React.FC<LabTrendChartProps> = ({ orders, metricName, regex, color = '#4F46E5' }) => {
  const chartRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // Parse data
    const data: { date: Date; value: number }[] = [];
    
    orders.forEach(order => {
      if (order.department === 'LAB' && order.status === 'COMPLETED' && order.results) {
        const match = order.results.match(regex);
        if (match && match[1]) {
          const value = parseFloat(match[1]);
          if (!isNaN(value)) {
            data.push({
              date: new Date(order.createdAt),
              value: value
            });
          }
        }
      }
    });

    // Sort by date
    data.sort((a, b) => a.date.getTime() - b.date.getTime());

    // If not enough data points, don't render chart
    if (data.length < 2) {
      d3.select(chartRef.current).selectAll('*').remove();
      return;
    }

    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    const width = 300;
    const height = 120;
    const margin = { top: 20, right: 20, bottom: 20, left: 30 };

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("width", "100%")
      .attr("height", "100%");

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3.scaleTime()
      .domain(d3.extent(data, d => d.date) as [Date, Date])
      .range([0, innerWidth]);

    // Ensure there's a bit of padding on the Y scale
    const yExtent = d3.extent(data, d => d.value) as [number, number];
    const yPadding = (yExtent[1] - yExtent[0]) * 0.1 || 1;
    
    const y = d3.scaleLinear()
      .domain([Math.max(0, yExtent[0] - yPadding), yExtent[1] + yPadding])
      .range([innerHeight, 0]);

    const line = d3.line<{ date: Date; value: number }>()
      .x(d => x(d.date))
      .y(d => y(d.value))
      .curve(d3.curveMonotoneX);

    // X Axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x).ticks(3).tickFormat(d => d3.timeFormat("%b %Y")(d as Date)))
      .call(g => g.select(".domain").attr("stroke", "#CBD5E1"))
      .call(g => g.selectAll(".tick line").attr("stroke", "#CBD5E1"))
      .call(g => g.selectAll(".tick text").attr("fill", "#64748B").attr("font-size", "8px"));

    // Y Axis
    g.append("g")
      .call(d3.axisLeft(y).ticks(4))
      .call(g => g.select(".domain").remove())
      .call(g => g.selectAll(".tick line").attr("stroke", "#E2E8F0").attr("x2", innerWidth))
      .call(g => g.selectAll(".tick text").attr("fill", "#64748B").attr("font-size", "8px").attr("x", -5));

    // Path
    g.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", color)
      .attr("stroke-width", 2)
      .attr("d", line);

    // Dots
    g.selectAll(".dot")
      .data(data)
      .enter().append("circle")
      .attr("class", "dot")
      .attr("cx", d => x(d.date))
      .attr("cy", d => y(d.value))
      .attr("r", 3)
      .attr("fill", "white")
      .attr("stroke", color)
      .attr("stroke-width", 1.5);
      
    // Tooltips or labels
    g.selectAll(".label")
      .data(data)
      .enter().append("text")
      .attr("x", d => x(d.date))
      .attr("y", d => y(d.value) - 8)
      .attr("fill", "#0F172A")
      .attr("font-size", "8px")
      .attr("font-weight", "bold")
      .attr("text-anchor", "middle")
      .text(d => d.value);

  }, [orders, metricName, regex, color]);

  // Determine if we have enough data to show
  const hasData = orders.filter(o => o.department === 'LAB' && o.status === 'COMPLETED' && o.results && regex.test(o.results)).length >= 2;

  if (!hasData) return null;

  return (
    <div className="bg-white border text-center border-slate-200 rounded-lg p-3 shadow-sm mb-4">
      <h4 className="text-[10px] uppercase font-bold text-slate-800 tracking-wider mb-2 text-left">{metricName} Trend</h4>
      <div className="w-full" style={{ maxWidth: '300px', margin: '0 auto' }}>
        <svg ref={chartRef}></svg>
      </div>
    </div>
  );
};
